import { NodeMaterialDto } from '@api/apiClient';

/**
 * Merges updated materials into an existing array, upserting by nodeId.
 * 
 * Use cases:
 * - Updating IndexedDB cache after material assignments
 * - Synchronizing LCAoMService changes with persistent storage
 * 
 * @param existing - Existing materials from IDB or server
 * @param updates - Updated/new materials from LCAoMService
 * @returns Merged array with updates applied
 * 
 * @example
 * ```ts
 * const existingFromIDB = await readModelData<NodeMaterialDto[]>('scenarioNodeMaterials', modelId, scenarioId);
 * const changedMaterials = lcaoMService.getChangedMaterialsByModel(scenarioId, [modelId])[modelId];
 * const merged = mergeMaterials(existingFromIDB ?? [], changedMaterials ?? []);
 * await writeModelData('scenarioNodeMaterials', modelId, scenarioId, merged);
 * ```
 */
export function mergeMaterials(
  existing: NodeMaterialDto[],
  updates: NodeMaterialDto[]
): NodeMaterialDto[] {
  const map = new Map<number, NodeMaterialDto>();

  // Add existing materials first
  for (const material of existing) {
    if (material.nodeId !== undefined) {
      map.set(material.nodeId, material);
    }
  }

  // Upsert updates (overwrites existing with same nodeId)
  for (const material of updates) {
    if (material.nodeId !== undefined) {
      map.set(material.nodeId, material);
    }
  }

  return Array.from(map.values());
}

/**
 * Removes materials by nodeId from an array.
 * 
 * Use cases:
 * - Removing deleted materials from IndexedDB cache
 * 
 * @param materials - Array of materials to filter
 * @param nodeIdsToRemove - Array of nodeIds to remove
 * @returns Filtered array with specified materials removed
 */
export function removeMaterialsByNodeId(
  materials: NodeMaterialDto[],
  nodeIdsToRemove: number[]
): NodeMaterialDto[] {
  const removeSet = new Set(nodeIdsToRemove);
  return materials.filter(m => m.nodeId === undefined || !removeSet.has(m.nodeId));
}
